import React from "react";
import CommonButton from "../../CommonButton";
import { PawPrint } from "lucide-react";
import { IPetDashboard } from "../../../types/Response/CustomerResponse";

interface PetCardComponentProps {
  data: IPetDashboard[];
  handleBooknow: (data: IPetDashboard) => void;
  showButton?: boolean;
}

const PetCardCustomerComponent: React.FC<PetCardComponentProps> = ({
  data,
  handleBooknow,
  showButton = true,
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6 px-2">
      {data?.map((pet: any, index: number) => {

        // ============================================================
        // NORMALIZE ID
        // Supports both API formats:
        // id / Id / petId / PetId
        // ============================================================

        const petId =
          pet?.id ??
          pet?.Id ??
          pet?.petId ??
          pet?.PetId;

        // ============================================================
        // NORMALIZE OTHER FIELDS
        // ============================================================

        const petName =
          pet?.name ??
          pet?.Name ??
          pet?.petName ??
          "Unnamed Pet";

        const imageUrl =
          pet?.imageUrl ??
          pet?.ImageUrl ??
          pet?.productImage ??
          "";

        const breed =
          pet?.breed ??
          pet?.Breed ??
          "";

        const gender =
          pet?.gender ??
          pet?.Gender ??
          "";

        const tag =
          pet?.tag ??
          pet?.Tag ??
          "";

        const isPetActive =
          pet?.isActive !== undefined
            ? !!pet.isActive
            : true;

        if (!isPetActive) {
          return null;
        }

        // ============================================================
        // IMPORTANT:
        // Create one normalized object.
        //
        // Your handleClickDog can continue using data.Id.
        // ============================================================

        const normalizedPet = {
          ...pet,

          Id: petId,
          id: petId,

          Name: petName,
          name: petName,

          ImageUrl: imageUrl,
          imageUrl: imageUrl,

          Breed: breed,
          breed: breed,

          Gender: gender,
          gender: gender,

          Tag: tag,
          tag: tag,
        } as IPetDashboard;

        // ============================================================
        // CLICK
        // ============================================================

        const handlePetClick = () => {
          if (
            petId === undefined ||
            petId === null ||
            petId === ""
          ) {
            console.error(
              "❌ Pet ID missing:",
              pet
            );

            return;
          }

          console.log(
            "✅ Opening pet:",
            petId,
            normalizedPet
          );

          handleBooknow(normalizedPet);
        };

        // ============================================================
        // BUTTON CLICK
        // Prevent card click from firing twice
        // ============================================================

        const handleAdoptClick = (
          e: React.MouseEvent
        ) => {
          e.stopPropagation();

          if (
            petId === undefined ||
            petId === null ||
            petId === ""
          ) {
            console.error(
              "❌ Pet ID missing:",
              pet
            );

            return;
          }

          console.log(
            "✅ Adopt Now:",
            petId,
            normalizedPet
          );

          handleBooknow(normalizedPet);
        };

        return (
          <div
            key={
              petId ??
              `pet-${index}`
            }
            onClick={handlePetClick}
            className="bg-white rounded-2xl overflow-hidden group hover:shadow-md transition-all duration-300 border border-slate-100 flex flex-col cursor-pointer relative"
          >

            {/* IMAGE */}

            <div className="relative h-48 sm:h-52 overflow-hidden bg-slate-50">

              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={petName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                  <PawPrint size={40} />
                </div>
              )}

            </div>

            {/* CONTENT */}

            <div className="p-4 flex-1 flex flex-col justify-between space-y-4 bg-white">

              <div className="space-y-2">

                {/* NAME + GENDER */}

                <div className="flex justify-between items-start gap-2">

                  <h3 className="text-base md:text-lg font-bold text-slate-900 tracking-tight line-clamp-1 group-hover:text-mainBrandColor transition-colors">
                    {petName}
                  </h3>

                  {gender && (
                    <span
                      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md font-bold shrink-0 border ${
                        String(gender).toLowerCase() ===
                        "female"
                          ? "bg-rose-50 text-rose-700 border-rose-100"
                          : "bg-sky-50 text-sky-700 border-sky-100"
                      }`}
                    >
                      {gender}
                    </span>
                  )}

                </div>

                {/* TAGS */}

                <div className="flex items-center gap-1.5 flex-wrap">

                  {breed && (
                    <span className="inline-flex items-center gap-1 bg-slate-50 text-slate-600 text-xs font-medium px-2.5 py-1 rounded-lg border border-slate-100">
                      <PawPrint
                        size={12}
                        className="text-slate-400"
                      />

                      {breed}
                    </span>
                  )}

                  {tag && (
                    <div className="bg-white/95 backdrop-blur-md px-2.5 py-1 rounded-lg text-[10px] font-bold text-slate-900 uppercase tracking-wider shadow-xs border border-slate-100">
                      {tag}
                    </div>
                  )}

                </div>

              </div>

              {/* BUTTON */}

              {showButton && (
                <div className="pt-2">

                  <CommonButton
                    text="Adopt Now"
                    variant="filled"
                    rounded="md"
                    className="w-full bg-mainBrandColor text-white transition-all transform active:scale-[0.99]"
                    textContainerClassName="py-2 text-xs uppercase tracking-wider font-bold"
                    onClick={()=>handleAdoptClick}
                  />

                </div>
              )}

            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PetCardCustomerComponent;